/**
 * Google Analytics Event Tracking Utility
 *
 * Centralized analytics tracking for Fig Careers
 * GA4 Property ID: G-5LTYVN6SF8
 */

// Helper to safely call gtag
const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// ============================================================================
// CHAT EVENTS
// ============================================================================

/**
 * Track when a user sends a chat message
 */
export const trackChatMessage = (conversationState, messageLength) => {
  gtag('event', 'chat_message_sent', {
    event_category: 'Chat',
    event_label: conversationState,
    conversation_state: conversationState,
    message_length: messageLength,
  });
};

/**
 * Track chat conversation completion
 */
export const trackChatComplete = (totalMessages) => {
  gtag('event', 'chat_conversation_complete', {
    event_category: 'Chat',
    event_label: 'Conversation Complete',
    total_messages: totalMessages,
  });
};

/**
 * Track when chat transitions to roadmap
 */
export const trackChatToRoadmap = () => {
  gtag('event', 'chat_to_roadmap', {
    event_category: 'Chat',
    event_label: 'Navigated to Roadmap',
  });
};

// ============================================================================
// ROADMAP EVENTS
// ============================================================================

/**
 * Track when career paths are generated
 */
export const trackRoadmapGenerated = (pathTypes = []) => {
  gtag('event', 'roadmap_generated', {
    event_category: 'Roadmap',
    event_label: 'Career Paths Generated',
    path_types: pathTypes.join(','),
    path_count: pathTypes.length,
  });
};

/**
 * Track when a user selects a career path (stable/adventurous/risky)
 */
export const trackPathSelected = (pathType) => {
  gtag('event', 'path_selected', {
    event_category: 'Roadmap',
    event_label: pathType,
    path_type: pathType,
  });
};

/**
 * Track when a roadmap is saved
 */
export const trackRoadmapSaved = (roadmapId, isNew = true) => {
  gtag('event', 'roadmap_saved', {
    event_category: 'Roadmap',
    event_label: isNew ? 'New Roadmap' : 'Updated Roadmap',
    roadmap_id: roadmapId,
    is_new: isNew,
  });
};

/**
 * Track when a roadmap is edited with AI
 */
export const trackRoadmapEdited = (instruction) => {
  gtag('event', 'roadmap_edited', {
    event_category: 'Roadmap',
    event_label: 'AI Edit',
    instruction_length: instruction?.length || 0,
  });
};

/**
 * Track when next roles are requested
 */
export const trackNextRolesRequested = (currentRole) => {
  gtag('event', 'next_roles_requested', {
    event_category: 'Roadmap',
    event_label: currentRole || 'Unknown Role',
  });
};

/**
 * Track role node click/expansion
 */
export const trackRoleNodeClicked = (roleTitle, pathType, year) => {
  gtag('event', 'role_node_clicked', {
    event_category: 'Roadmap',
    event_label: roleTitle,
    path_type: pathType,
    year: year,
  });
};

// ============================================================================
// EXPORT & SHARE EVENTS
// ============================================================================

/**
 * Track PDF export
 */
export const trackPDFExport = () => {
  gtag('event', 'pdf_exported', {
    event_category: 'Export',
    event_label: 'PDF Download',
  });
};

/**
 * Track career card download
 */
export const trackCardDownload = (templateRarity) => {
  gtag('event', 'card_downloaded', {
    event_category: 'Export',
    event_label: 'Career Card PNG',
    template_rarity: templateRarity || 'default',
  });
};

/**
 * Track career card share
 */
export const trackCardShare = (shareMethod) => {
  gtag('event', 'card_shared', {
    event_category: 'Share',
    event_label: shareMethod,
    share_method: shareMethod, // 'native', 'clipboard', 'download'
  });
};

// ============================================================================
// BUTTON CLICK EVENTS
// ============================================================================

/**
 * Track generic button clicks
 */
export const trackButtonClick = (buttonName, location) => {
  gtag('event', 'button_click', {
    event_category: 'Button',
    event_label: buttonName,
    button_name: buttonName,
    location: location,
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName, page) => {
  gtag('event', 'cta_click', {
    event_category: 'CTA',
    event_label: ctaName,
    cta_name: ctaName,
    page: page,
  });
};

/**
 * Track navigation button clicks
 */
export const trackNavigation = (source, destination) => {
  gtag('event', 'navigation', {
    event_category: 'Navigation',
    event_label: `${source} -> ${destination}`,
    source_page: source,
    destination_page: destination,
  });
};

// ============================================================================
// USER JOURNEY EVENTS
// ============================================================================

/**
 * Track onboarding step completion
 */
export const trackOnboardingStep = (stepName, stepNumber) => {
  gtag('event', 'onboarding_step', {
    event_category: 'Onboarding',
    event_label: stepName,
    step_name: stepName,
    step_number: stepNumber,
  });
};

/**
 * Track resume upload
 */
export const trackResumeUpload = (fileType) => {
  gtag('event', 'resume_uploaded', {
    event_category: 'Onboarding',
    event_label: fileType,
    file_type: fileType,
  });
};

/**
 * Track sign in/sign up
 */
export const trackAuth = (action, method) => {
  gtag('event', action, {
    event_category: 'Auth',
    event_label: method,
    auth_method: method, // 'google', 'email', etc.
  });
};

/**
 * Track sign out
 */
export const trackSignOut = () => {
  gtag('event', 'sign_out', {
    event_category: 'Auth',
    event_label: 'User Signed Out',
  });
};

// ============================================================================
// DISCOVERY & JOBS EVENTS
// ============================================================================

/**
 * Track role discovery search
 */
export const trackDiscoverSearch = (searchQuery) => {
  gtag('event', 'discover_search', {
    event_category: 'Discovery',
    event_label: searchQuery || 'Browse',
    search_query: searchQuery,
  });
};

/**
 * Track job search
 */
export const trackJobSearch = (filters) => {
  gtag('event', 'job_search', {
    event_category: 'Jobs',
    event_label: filters?.role || 'General Search',
    ...filters,
  });
};

// ============================================================================
// PAGE VIEW TRACKING
// ============================================================================

/**
 * Track virtual page views (for SPA navigation)
 */
export const trackPageView = (pagePath, pageTitle) => {
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

// ============================================================================
// ERROR TRACKING
// ============================================================================

/**
 * Track errors for debugging
 */
export const trackError = (errorType, errorMessage, location) => {
  gtag('event', 'error', {
    event_category: 'Error',
    event_label: errorType,
    error_type: errorType,
    error_message: errorMessage?.substring(0, 100),
    location: location,
  });
};

export default {
  trackChatMessage,
  trackChatComplete,
  trackChatToRoadmap,
  trackRoadmapGenerated,
  trackPathSelected,
  trackRoadmapSaved,
  trackRoadmapEdited,
  trackNextRolesRequested,
  trackRoleNodeClicked,
  trackPDFExport,
  trackCardDownload,
  trackCardShare,
  trackButtonClick,
  trackCTAClick,
  trackNavigation,
  trackOnboardingStep,
  trackResumeUpload,
  trackAuth,
  trackSignOut,
  trackDiscoverSearch,
  trackJobSearch,
  trackPageView,
  trackError,
};
